import axios from "axios";
import { UserKeys } from "src/constants/userDetails";
import { API_URL, GLOBAL_SERVICE_URL } from "src/scenes/App";

export type MeetingStatus = "scheduled" | "starting" | "active" | "ended" | "cancelled" | "expired";
export type AdmissionStatus = "invited" | "waiting" | "admitted" | "declined" | "left" | "removed";

export interface PublicMeeting {
    meetingToken: string;
    occurrenceUuid: string;
    nativeMeetingUrl: string;
    region: string;
    title: string;
    hostName: string;
    businessName: string | null;
    scheduledStart: string | null;
    scheduledEnd: string | null;
    timezone: string;
    status: MeetingStatus;
    passwordRequired: boolean;
    connectedParticipantCount: number;
    guestApprovalRequired: true;
}

export interface MeetingParticipant {
    participantUuid: string;
    employeeUuid: string | null;
    email: string | null;
    displayName: string;
    participantType: "host" | "internal" | "guest";
    invitationStatus: string;
    admissionStatus: AdmissionStatus;
    matrixUserId: string | null;
    joinedAt: string | null;
    leftAt: string | null;
}

export interface MeetingInvitationDelivery {
    deliveryUuid: string;
    participantUuid: string;
    email: string | null;
    displayName: string;
    status: "queued" | "accepted" | "delivered" | "failed";
    failureReason: string | null;
    sequence: number;
    recurrenceId: string | null;
    queuedAt: string | null;
    acceptedAt: string | null;
    deliveredAt: string | null;
    failedAt: string | null;
}

export interface MeetingDetail {
    canManage: boolean;
    canStart: boolean;
    canJoin?: boolean;
    waitingForHost?: boolean;
    earliestStartAt: string | null;
    canShareLink?: boolean;
    canReadNotes?: boolean;
    meetingUuid: string;
    meetingToken: string;
    meetingUrl: string;
    nativeMeetingUrl: string;
    region: string;
    title: string;
    description: string | null;
    hostEmployeeUuid: string;
    hostName: string;
    calendarItemUuid: string | null;
    chatUuid: string | null;
    scheduledStart: string | null;
    scheduledEnd: string | null;
    timezone: string;
    repeat: string;
    recurrenceEndsAt: string | null;
    status: MeetingStatus;
    passwordEnabled: boolean;
    guestApprovalRequired: true;
    matrixRoomId: string | null;
    maxParticipants: number;
    connectedParticipantCount: number;
    participants: MeetingParticipant[];
    occurrence: {
        occurrenceUuid: string;
        recurrenceId: string;
        scheduledStart: string | null;
        scheduledEnd: string | null;
        status: MeetingStatus;
        matrixRoomId: string | null;
        startedAt: string | null;
        endedAt: string | null;
    };
}

export type MeetingListItem = Omit<MeetingDetail, "participants"> & {
    participants?: MeetingParticipant[];
};

export interface MeetingListResponse {
    meetings: MeetingListItem[];
    page: number;
    nextPage: number | null;
    total: number;
}

export type MeetingOccurrenceAccess = MeetingDetail["occurrence"] & {
    meetingUuid: string;
    title: string;
    canReadNotes: boolean;
    notesRoomId: string | null;
};

export interface GuestMeetingSession {
    guestSessionUuid: string;
    sessionToken?: string;
    status: AdmissionStatus;
    expiresAt: string;
    matrix?: {
        userId: string;
        deviceId: string;
        accessToken: string;
        roomId: string;
    };
}

const getAuthConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem(UserKeys.TOKEN) || ""}` },
});

type RegionalApi = {
    apiUrl?: string;
};

let resolvedPublicMeetingApiUrl: string | null = null;

const trimTrailingSlash = (url: string): string => url.replace(/\/+$/, "");

const getLocalMeetingApiUrl = (): string | null => {
    if (window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1") {
        return null;
    }
    return `http://${window.location.hostname}:8880`;
};

export const getMeetingApiBaseUrl = (): string => (
    API_URL
    || resolvedPublicMeetingApiUrl
    || getLocalMeetingApiUrl()
    || window.location.origin
);

const appendOccurrence = (url: string, occurrenceUuid?: string): string => occurrenceUuid
    ? `${url}?occurrence=${encodeURIComponent(occurrenceUuid)}`
    : url;

const fetchPublicMeeting = async (
    apiUrl: string,
    meetingToken: string,
    occurrenceUuid?: string
): Promise<PublicMeeting> => (
    await axios.get<PublicMeeting>(appendOccurrence(
        `${trimTrailingSlash(apiUrl)}/meet/${encodeURIComponent(meetingToken)}`,
        occurrenceUuid
    ))
).data;

const discoverPublicMeeting = async (meetingToken: string, occurrenceUuid?: string): Promise<PublicMeeting> => {
    const configuredApiUrl = API_URL || getLocalMeetingApiUrl();
    if (configuredApiUrl) {
        resolvedPublicMeetingApiUrl = trimTrailingSlash(configuredApiUrl);
        return fetchPublicMeeting(resolvedPublicMeetingApiUrl, meetingToken, occurrenceUuid);
    }

    const regionResponse = await axios.get<RegionalApi[]>(`${GLOBAL_SERVICE_URL}/regions`);
    let lastError: unknown = null;
    for (const region of regionResponse.data) {
        if (!region.apiUrl) {
            continue;
        }
        const regionalApiUrl = trimTrailingSlash(region.apiUrl);
        try {
            const meeting = await fetchPublicMeeting(regionalApiUrl, meetingToken, occurrenceUuid);
            resolvedPublicMeetingApiUrl = regionalApiUrl;
            return meeting;
        } catch (error) {
            lastError = error;
        }
    }

    if (lastError) {
        throw lastError;
    }
    throw new Error("No regional API is available for this meeting.");
};

export const getPublicMeeting = async (meetingToken: string, occurrenceUuid?: string): Promise<PublicMeeting> => (
    discoverPublicMeeting(meetingToken, occurrenceUuid)
);

export const requestGuestMeetingSession = async (
    meetingToken: string,
    displayName: string,
    password: string,
    browserIdentity: string,
    occurrenceUuid?: string
): Promise<GuestMeetingSession> => (
    await axios.post<GuestMeetingSession>(appendOccurrence(
        `${getMeetingApiBaseUrl()}/meet/${encodeURIComponent(meetingToken)}/guest-sessions`,
        occurrenceUuid
    ), {
        displayName,
        password,
        browserIdentity,
    })
).data;

export const getGuestMeetingSession = async (
    meetingToken: string,
    sessionToken: string
): Promise<GuestMeetingSession> => (
    await axios.get<GuestMeetingSession>(`${getMeetingApiBaseUrl()}/meet/${encodeURIComponent(meetingToken)}/guest-sessions/current`, {
        headers: { "X-Meeting-Session": sessionToken },
    })
).data;

export const leaveGuestMeeting = async (meetingToken: string, sessionToken: string): Promise<void> => {
    await axios.post(
        `${getMeetingApiBaseUrl()}/meet/${encodeURIComponent(meetingToken)}/guest-sessions/current/leave`,
        {},
        { headers: { "X-Meeting-Session": sessionToken } }
    );
};

export const createInstantMeeting = async (chatUuid: string, callId: string): Promise<MeetingDetail> => (
    await axios.post<MeetingDetail>(
        `${API_URL}/meetings/instant/${encodeURIComponent(chatUuid)}`,
        { callId },
        getAuthConfig()
    )
).data;

export const getMeeting = async (meetingUuid: string): Promise<MeetingDetail> => (
    await axios.get<MeetingDetail>(`${API_URL}/meetings/${encodeURIComponent(meetingUuid)}`, getAuthConfig())
).data;

export const getAuthenticatedMeetingByToken = async (
    meetingToken: string,
    occurrenceUuid?: string
): Promise<MeetingDetail> => (
    await axios.get<MeetingDetail>(
        appendOccurrence(`${API_URL}/meetings/by-token/${encodeURIComponent(meetingToken)}`, occurrenceUuid),
        getAuthConfig()
    )
).data;

export const listMeetings = async (
    section: "scheduled" | "past",
    page = 1,
    perPage = 20
): Promise<MeetingListResponse> => (
    await axios.get<MeetingListResponse>(`${API_URL}/meetings`, {
        ...getAuthConfig(),
        params: { section, page, perPage },
    })
).data;

export const getMeetingOccurrence = async (
    meetingUuid: string,
    occurrenceUuid: string
): Promise<MeetingOccurrenceAccess> => (
    await axios.get<MeetingOccurrenceAccess>(
        `${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/occurrences/${encodeURIComponent(occurrenceUuid)}`,
        getAuthConfig()
    )
).data;

export const startMeeting = async (meetingUuid: string): Promise<MeetingDetail> => (
    await axios.post<MeetingDetail>(`${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/start`, {}, getAuthConfig())
).data;

export const startMeetingOccurrence = async (
    meetingUuid: string,
    occurrenceUuid: string
): Promise<MeetingDetail> => (
    await axios.post<MeetingDetail>(
        `${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/occurrences/${encodeURIComponent(occurrenceUuid)}/start`,
        {},
        getAuthConfig()
    )
).data;

export const joinInternalMeeting = async (meetingUuid: string): Promise<MeetingDetail> => (
    await axios.post<MeetingDetail>(`${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/join`, {}, getAuthConfig())
).data;

export const joinInternalMeetingOccurrence = async (
    meetingUuid: string,
    occurrenceUuid: string
): Promise<MeetingDetail> => (
    await axios.post<MeetingDetail>(
        `${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/occurrences/${encodeURIComponent(occurrenceUuid)}/join`,
        {},
        getAuthConfig()
    )
).data;

export const joinInternalMeetingByToken = async (
    meetingToken: string,
    occurrenceUuid?: string
): Promise<MeetingDetail> => (
    await axios.post<MeetingDetail>(
        appendOccurrence(`${API_URL}/meetings/by-token/${encodeURIComponent(meetingToken)}/join`, occurrenceUuid),
        {},
        getAuthConfig()
    )
).data;

export const leaveInternalMeeting = async (meetingUuid: string): Promise<MeetingParticipant> => (
    await axios.post<MeetingParticipant>(
        `${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/leave`,
        {},
        getAuthConfig()
    )
).data;

export const leaveInternalMeetingOccurrence = async (
    meetingUuid: string,
    occurrenceUuid: string
): Promise<MeetingParticipant> => (
    await axios.post<MeetingParticipant>(
        `${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/occurrences/${encodeURIComponent(occurrenceUuid)}/leave`,
        {},
        getAuthConfig()
    )
).data;

export const inviteMeetingGuests = async (
    meetingUuid: string,
    guestEmails: string[],
    password: string
): Promise<MeetingParticipant[]> => (
    await axios.post<MeetingParticipant[]>(
        `${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/guest-invitations`,
        { guestEmails, password },
        getAuthConfig()
    )
).data;

export const getMeetingInvitationDeliveries = async (
    meetingUuid: string
): Promise<MeetingInvitationDelivery[]> => (
    await axios.get<MeetingInvitationDelivery[]>(
        `${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/guest-invitations`,
        getAuthConfig()
    )
).data;

export const retryMeetingInvitation = async (
    meetingUuid: string,
    deliveryUuid: string
): Promise<MeetingInvitationDelivery> => (
    await axios.post<MeetingInvitationDelivery>(
        `${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/guest-invitations/${encodeURIComponent(deliveryUuid)}/retry`,
        {},
        getAuthConfig()
    )
).data;

export const updateMeetingAdmission = async (
    meetingUuid: string,
    participantUuid: string,
    status: "admitted" | "declined"
): Promise<MeetingParticipant> => (
    await axios.put<MeetingParticipant>(
        `${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/participants/${encodeURIComponent(participantUuid)}/admission`,
        { status },
        getAuthConfig()
    )
).data;

export const endMeeting = async (meetingUuid: string): Promise<MeetingDetail> => (
    await axios.post<MeetingDetail>(`${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/end`, {}, getAuthConfig())
).data;

export const endMeetingOccurrence = async (
    meetingUuid: string,
    occurrenceUuid: string
): Promise<MeetingDetail> => (
    await axios.post<MeetingDetail>(
        `${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/occurrences/${encodeURIComponent(occurrenceUuid)}/end`,
        {},
        getAuthConfig()
    )
).data;

export const cancelMeetingOccurrence = async (
    meetingUuid: string,
    occurrenceUuid: string
): Promise<MeetingDetail> => (
    await axios.post<MeetingDetail>(
        `${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/occurrences/${encodeURIComponent(occurrenceUuid)}/cancel`,
        {},
        getAuthConfig()
    )
).data;

export const cancelMeetingOccurrenceAndFollowing = async (
    meetingUuid: string,
    occurrenceUuid: string
): Promise<MeetingDetail> => (
    await axios.post<MeetingDetail>(
        `${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/occurrences/${encodeURIComponent(occurrenceUuid)}/cancel-following`,
        {},
        getAuthConfig()
    )
).data;

export const rescheduleMeetingOccurrence = async (
    meetingUuid: string,
    occurrenceUuid: string,
    scheduledStart: string,
    scheduledEnd: string,
    following = false
): Promise<MeetingDetail> => (
    await axios.put<MeetingDetail>(
        `${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/occurrences/${encodeURIComponent(occurrenceUuid)}${following ? "/following" : ""}`,
        { scheduledStart, scheduledEnd },
        getAuthConfig()
    )
).data;

export const removeMeetingParticipant = async (
    meetingUuid: string,
    participantUuid: string
): Promise<MeetingParticipant> => (
    await axios.delete<MeetingParticipant>(
        `${API_URL}/meetings/${encodeURIComponent(meetingUuid)}/participants/${encodeURIComponent(participantUuid)}`,
        getAuthConfig()
    )
).data;
